import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { Box } from '@gemini/core';
import { Button } from '@gemini/ui';
import { MFE_IDENTIFIER } from '@price-pages-agency-cards/src/constants';
import { GERMAN_PROFILE_ANCHOR, mediums } from '@price-pages-agency-cards/src/constants/tracking';
import DE_TRANSLATIONS from '@price-pages-agency-cards/src/locales/de/translation.json';
import EN_TRANSLATIONS from '@price-pages-agency-cards/src/locales/en/translation.json';
import FR_TRANSLATIONS from '@price-pages-agency-cards/src/locales/fr/translation.json';
import NL_TRANSLATIONS from '@price-pages-agency-cards/src/locales/nl/translation.json';
import { sendAnalytics } from '@price-pages-agency-cards/src/presentation/helpers/tracking';
import { useAgencyStore } from '@price-pages-agency-cards/src/presentation/hooks/useAgencyHook';
import { generateAVItemPricePages } from '@shared-utils/helpers/tracking';
import { useBreakpoint } from '@shared-utils/helpers/viewport';
import { useLoadTranslations } from '@shared-utils/hooks/useLoadTranslations';
import { AV_LINK_TEXT, AV_LIST_NAME, AV_ZONE, TRACKING_EVENT_TYPE, } from '@shared-utils/types/analytics.types';
import Address from '@shared-utils/ui/components/Address';
import Logo from '@shared-utils/ui/components/Logo';
import PropertiesForSale from '@shared-utils/ui/components/PropertiesForSale';
import Reviews from '@shared-utils/ui/components/Reviews';
import Seniority from '@shared-utils/ui/components/Seniority';
import { SidePanelSkeleton } from '../SidePanelSkeleton';
const onClickTrackingEvent = (avItem) => {
    sendAnalytics({
        av_geolocation_level: '%av_geolocation_level%',
        av_items: avItem,
        av_zone: AV_ZONE.PRICE_SIDE_PANEL,
        event: TRACKING_EVENT_TYPE.SELECT_ITEM,
        link_text: AV_LINK_TEXT.LOGO,
    });
};
const SidePanelCardList = () => {
    useLoadTranslations(MFE_IDENTIFIER, {
        de: DE_TRANSLATIONS,
        en: EN_TRANSLATIONS,
        fr: FR_TRANSLATIONS,
        nl: NL_TRANSLATIONS,
    });
    const { t } = useTranslation(MFE_IDENTIFIER);
    const { isMobile } = useBreakpoint();
    const getRecommendedAgencies = useAgencyStore((state) => state.getRecommendedAgencies);
    const isFetchRecommendedAgenciesLoading = useAgencyStore((state) => state.isFetchRecommendedAgenciesLoading);
    const agencies = getRecommendedAgencies();
    const avItems = agencies.map((agency, index) => generateAVItemPricePages(agency, AV_LIST_NAME.PRICE_MAP_RECOMMENDED_AGENCY_SIDE, index));
    useEffect(() => {
        if (agencies.length && !isFetchRecommendedAgenciesLoading) {
            sendAnalytics({
                av_geolocation_level: '%av_geolocation_level%',
                av_items: avItems,
                av_zone: AV_ZONE.PRICE_SIDE_PANEL,
                event: TRACKING_EVENT_TYPE.VIEW_ITEM_LIST,
            });
        }
    }, [agencies.length, avItems, isFetchRecommendedAgenciesLoading]);
    if (isFetchRecommendedAgenciesLoading)
        return _jsx(SidePanelSkeleton, {});
    if (!agencies.length)
        return null;
    return (_jsxs(Box, { children: [_jsx(Box, { marginBottom: "spacing.24", typography: "typography.headline.20.bold", children: t('wlac_pricepages_head_title') }), agencies.map((agency, index) => {
                const { agencyLogo, agencyName, profileUrl, address: { zipCode, city }, reviews, propertiesForSale, seniority, distance, } = agency;
                const { mediumContact, mediumLogo } = mediums;
                const avItem = [
                    generateAVItemPricePages(agency, AV_LIST_NAME.PRICE_MAP_RECOMMENDED_AGENCY_SIDE, 0),
                ];
                return (_jsxs(Box, { alignItems: "center", backgroundColor: "color.background.default", borderBottomStyle: index === agencies.length - 1 ? 'solid' : 'none', borderColor: "color.background.subdued", borderTopStyle: "solid", borderWidth: "borderWidth.1", columnGap: "spacing.16", display: "grid", gridTemplateColumns: "auto 1fr", paddingVertical: "spacing.16", rowGap: "spacing.24", width: "100%", children: [_jsx(Logo, { agencyLogo: agencyLogo, agencyName: agencyName, linkUrl: `${profileUrl}${mediumLogo}${GERMAN_PROFILE_ANCHOR}`, onClick: () => onClickTrackingEvent(avItem) }), _jsxs(Box, { display: "flex", flexDirection: "column", gap: "spacing.8", gridColumn: "2", children: [_jsxs(Box, { display: "flex", flexWrap: "nowrap", justifyContent: "space-between", children: [_jsx(Box, { typography: "typography.body.16.bold", children: agencyName }), seniority && (_jsx(Box, { children: _jsx(Seniority, { startYear: seniority, t: t }) }))] }), _jsx(Address, { city: city, distance: distance, t: t, zipCode: zipCode, isGreyContent: true }), _jsxs(Box, { display: "flex", flexDirection: isMobile ? 'column' : 'row', gap: "spacing.8", children: [reviews && (_jsx(Reviews, { reviews: {
                                                avgRating: reviews?.avgRating,
                                            } })), _jsx(PropertiesForSale, { propertiesCount: propertiesForSale, t: t })] })] }), _jsx(Box, { gridColumn: "1 / -1", children: _jsx(Button, { href: `${profileUrl}${mediumContact}${GERMAN_PROFILE_ANCHOR}`, size: "40", variant: "secondary", isExternal: true, isFullWidth: true, onPress: () => {
                                    sendAnalytics({
                                        av_geolocation_level: '%av_geolocation_level%',
                                        av_items: avItem,
                                        av_zone: AV_ZONE.PRICE_SIDE_PANEL,
                                        event: TRACKING_EVENT_TYPE.SELECT_ITEM,
                                        link_text: AV_LINK_TEXT.CONTACT_CTA,
                                    });
                                }, children: t('wlac_primarycta') }) })] }, agency.id));
            })] }));
};
export default SidePanelCardList;
